import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { BatchResultResponse, RequestWithRelationsFromRequestsList } from '@/models/request';
import { createBEMClasses } from '@/utils/classname';

import { BULK_FAILURE_REASON_MESSAGES } from './bulk-failure-reasons';

import './BulkDialogs.css';

const { element } = createBEMClasses('bulk-dialogs');

export const BulkResultView = ({
  result,
  requests,
}: {
  result: BatchResultResponse;
  requests: RequestWithRelationsFromRequestsList[];
}) => {
  const { i18n } = useLingui();

  const requestsById = new Map(requests.map((request) => [request.id, request]));
  const requestsByArticleId = new Map(
    requests.flatMap((request) =>
      request.allArticles.map((article) => [article.id, request] as const)
    )
  );

  return (
    <Stack gap="0.75rem">
      <Paragraph>
        {result.succeeded.length === 0 ? (
          <Trans id="requests.bulk.result.intro.all-failed">No request could be updated.</Trans>
        ) : (
          <Trans id="requests.bulk.result.intro">
            Some requests could not be updated. The others were updated.
          </Trans>
        )}
      </Paragraph>
      <ul className={element('failures')}>
        {result.failed.map((failure) => {
          const request = requestsById.get(failure.id) ?? requestsByArticleId.get(failure.id);
          const requestReference = request?.reference ?? failure.id;
          const reason = i18n._(BULK_FAILURE_REASON_MESSAGES[failure.reason]);

          return (
            <li key={failure.id} className={element('failure')}>
              <Paragraph weight="medium">{requestReference}</Paragraph>
              <Paragraph color="secondary">{reason}</Paragraph>
            </li>
          );
        })}
      </ul>
    </Stack>
  );
};
